// ===========================================
// Lesson window selects — "Starts in week" / "Last week"
// ===========================================
// A class may begin part-way through a term or finish before its end. The window belongs to ONE
// term's row (the ANCHOR term); the other terms of a multi-term class run the full term.
//
// ⚠️ Both option lists derive from `termWeekCount(` of the anchor term — never a raw date
// subtraction or the transport ceiling (#169). Labels are `Week 5 09/11/2026` via
// `formatTermWeekDate` (CLAUDE.md Critical Rule 7); the class's own weekday is used when it has one,
// otherwise the week's Monday.

import { Grid, FormControl, InputLabel, Select, MenuItem, FormHelperText } from '@mui/material';
import { termWeekCount, formatTermWeekDate } from '../../../utils/dateUtils';

interface WindowTerm {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
}

export interface LessonWindowSelectsProps {
  startWeek: number | null;
  endWeek: number | null;
  onChange: (updates: { startWeek?: number | null; endWeek?: number | null }) => void;
  /** The term the window applies to; undefined until a term is chosen. */
  anchorTerm: WindowTerm | undefined;
  /** How many terms the class spans/creates in — drives the "anchor only" caption. */
  selectedTermCount: number;
  /** The class's weekday (0-6) when it has a schedule; null → the week's Monday. */
  dayOfWeek: number | null;
  disabled?: boolean;
  /**
   * Editing, and the admin has UNTICKED the anchor term: its row is about to be deleted, so a
   * window set now would be thrown away. The selects lock and say so (/qa F3).
   */
  anchorBeingRemoved?: boolean;
}

const toValue = (n: number | null): string => (n === null ? '' : String(n));
const fromValue = (v: string): number | null => (v === '' ? null : Number(v));

export default function LessonWindowSelects({
  startWeek,
  endWeek,
  onChange,
  anchorTerm,
  selectedTermCount,
  dayOfWeek,
  disabled = false,
  anchorBeingRemoved = false,
}: LessonWindowSelectsProps) {
  const weeks = anchorTerm ? termWeekCount(anchorTerm.startDate, anchorTerm.endDate) : 0;
  const label = (week: number) => formatTermWeekDate(anchorTerm!.startDate, week, dayOfWeek);

  const firstWeek = startWeek ?? 1;
  // "Start" offers 2..N (1 is the default option); "Last week" offers firstWeek..N-1 (N is "Term end").
  const startOptions = Array.from({ length: Math.max(0, weeks - 1) }, (_, i) => i + 2);
  const endOptions = Array.from({ length: Math.max(0, weeks - firstWeek) }, (_, i) => firstWeek + i);

  // A stored week beyond the term (a term shortened after the fact) must still render, or MUI
  // shows a blank select and warns. It is offered as a labelled extra so the admin can see it.
  const startOutOfTerm = startWeek !== null && startWeek > weeks;
  const endOutOfTerm = endWeek !== null && endWeek > weeks;
  // The term's last week IS "Term end" (the server stores it as null).
  const shownEnd = endWeek !== null && endWeek === weeks ? null : endWeek;

  const helper =
    !anchorTerm
      ? 'Choose a term first.'
      : anchorBeingRemoved
        ? `${anchorTerm.name} is being removed from this class, so its first and last week can't be changed here.`
      : selectedTermCount > 1
        ? `Applies to ${anchorTerm.name} only; the other terms run the full term.`
        : undefined;

  return (
    <>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth disabled={disabled || !anchorTerm || anchorBeingRemoved}>
          <InputLabel id="lesson-start-week-label" shrink>
            Starts in week
          </InputLabel>
          <Select
            labelId="lesson-start-week-label"
            label="Starts in week"
            notched
            displayEmpty
            value={toValue(startWeek)}
            onChange={(e) => {
              const next = fromValue(e.target.value as string);
              // Keep "Last week" valid: a last week before the new first week is cleared.
              const clearEnd = next !== null && endWeek !== null && endWeek < next;
              onChange({ startWeek: next, ...(clearEnd ? { endWeek: null } : {}) });
            }}
            inputProps={{ 'data-testid': 'lesson-start-week' }}
          >
            <MenuItem value="">{anchorTerm ? 'Week 1 (term start)' : '—'}</MenuItem>
            {anchorTerm &&
              startOptions.map((w) => (
                <MenuItem key={w} value={String(w)}>
                  {label(w)}
                </MenuItem>
              ))}
            {anchorTerm && startOutOfTerm && (
              <MenuItem value={String(startWeek)}>{`Week ${startWeek} (past the term's end)`}</MenuItem>
            )}
          </Select>
          <FormHelperText>{helper ?? 'The first week this class runs.'}</FormHelperText>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth disabled={disabled || !anchorTerm || anchorBeingRemoved}>
          <InputLabel id="lesson-end-week-label" shrink>
            Last week
          </InputLabel>
          <Select
            labelId="lesson-end-week-label"
            label="Last week"
            notched
            displayEmpty
            value={toValue(shownEnd)}
            onChange={(e) => onChange({ endWeek: fromValue(e.target.value as string) })}
            inputProps={{ 'data-testid': 'lesson-end-week' }}
          >
            <MenuItem value="">{anchorTerm ? 'Term end' : '—'}</MenuItem>
            {anchorTerm &&
              endOptions.map((w) => (
                <MenuItem key={w} value={String(w)}>
                  {label(w)}
                </MenuItem>
              ))}
            {anchorTerm && endOutOfTerm && !endOptions.includes(endWeek as number) && (
              <MenuItem value={String(endWeek)}>{`Week ${endWeek} (past the term's end)`}</MenuItem>
            )}
          </Select>
          <FormHelperText>The last week this class runs.</FormHelperText>
        </FormControl>
      </Grid>
    </>
  );
}
